import { E_QUERY_ITEM_CONDITION, IListItem, IQueryItemField } from '@patsnap/synapse_common_interface'
import { maxDrugCount } from '@pharmsnap/shared/src/config'
import type { IBAcTag } from '@pharmsnap/shared/src/types/autocomplete'
import {
  drugFinderExpertReverseMap,
  DrugFinderInputKey,
  generateAllTag,
  generateHardData,
  generateInputItem,
  generateOrgTypeItem,
  generateTagItem,
  getLast30daysRange,
  getLast3MonthsRange,
  getLast7daysRange,
  getOneYearRange,
  ILogicKey,
  showSingleToast,
} from '@pharmsnap/shared/src/utils'
import { ref } from '@vue/composition-api'
import { cloneDeep } from 'lodash'
import { useLocale } from 'pharmsnapMF_shared/composition'

export type IQueryItemGroup = IQueryItemField

export type IFieldGroupFieldsItem = { tags?: IBAcTag[]; fields: string[]; selectedValues?: IBAcTag[] }

export interface IFieldGroup {
  id: string
  logic: 'AND' | 'OR' | 'NOT'
  type: 'simple' | 'combined'
  property?: string
  _meta?: any
  isNotFirstGroup?: boolean
  fields: {
    [key: string]: IFieldGroupFieldsItem
  }
}

export const fieldGroupsBasis = Object.freeze([
  {
    id: '1',
    logic: 'AND',
    type: 'simple',
    property: 'drug',
    fields: {
      drugItems: {
        fields: ['DRUG_ID'],
        tags: [],
      },
    },
  },
  {
    id: '2',
    logic: 'AND',
    type: 'simple',
    property: 'drugType',
    fields: {
      drugTypeItems: {
        fields: ['DRUG_TYPE'],
        tags: [],
      },
    },
  },
  {
    id: '3',
    logic: 'AND',
    type: 'simple',
    property: 'target',
    fields: {
      targetItems: {
        fields: ['TARGET_ID'],
        tags: [],
      },
    },
  },
  {
    id: '4',
    logic: 'AND',
    type: 'simple',
    property: 'atc',
    fields: {
      atcItems: { fields: ['ATC_CODE'], tags: [] },
    },
  },
  {
    id: '5',
    logic: 'AND',
    type: 'simple',
    property: 'chemicalModification',
    fields: {
      modificationItems: {
        fields: ['MODIFICATION_ID'],
        tags: [],
      },
    },
  },
  {
    id: '6',
    logic: 'AND',
    type: 'simple',
    property: 'keyword',
    fields: {
      keyword: { fields: ['ALL_FIELDS'], tags: [] },
    },
  },
  {
    id: '7',
    logic: 'AND',
    type: 'simple',
    property: 'deal',
    fields: {
      dealExistItems: {
        fields: ['EXIST_DEAL'],
        selectedValues: [generateAllTag()],
      },
    },
  },
  {
    id: '8',
    logic: 'AND',
    type: 'combined',
    property: 'indication',
    fields: {
      indicationItems: { fields: ['RESEARCH_DISEASE', 'NON_RESEARCH_DISEASE'], tags: [] },
      indicationPhaseItems: {
        fields: ['INDICATION_PHASE'],
        selectedValues: [generateAllTag()],
      },
    },
  },
  {
    id: '9',
    logic: 'AND',
    type: 'combined',
    property: 'moa',
    fields: {
      actionItems: {
        fields: ['ACTION_ID'],
        tags: [],
      },
      mechanismItems: {
        fields: ['MECHANISM_ACTION_ID'],
        tags: [],
      },
    },
  },
  {
    id: '10',
    logic: 'AND',
    type: 'combined',
    property: 'milestone',
    fields: {
      milestoneItems: {
        fields: ['MILESTONE_ID'],
        selectedValues: [generateAllTag()],
      },
      milestoneDate: { fields: ['MILESTONE_DATE'], selectedValues: [] },
    },
  },
  {
    id: '11',
    logic: 'AND',
    type: 'simple',
    property: 'highestPhase',
    fields: {
      highestPhaseItems: {
        fields: ['GLOBAL_HIGHEST_DEV_STATUS'],
        selectedValues: [generateAllTag()],
      },
    },
  },
  {
    id: '12',
    logic: 'AND',
    type: 'combined',
    property: 'organization',
    fields: {
      organizationItems: {
        fields: ['DEV_ORG_ID_ONEID', 'NON_DEV_ORG_ID_ONEID', 'ORIGINAL_DEV_ORG_ID_ONEID', 'LICENSE_ORG_ID_ONEID'],
        tags: [],
      },
      orgRoleItems: {
        fields: ['DEV_ORG_TYPE_ONEID'],
        selectedValues: [generateAllTag()],
      },
    },
  },
  {
    id: '13',
    logic: 'AND',
    type: 'combined',
    property: 'organizationProperty',
    fields: {
      organizationTypeItems: {
        fields: ['ORG_TYPE_ONEID'],
        selectedValues: [generateAllTag()],
      },
      employeeNumberItems: {
        fields: ['ORG_EMP_NUMBER_GENERAL_ONEID'],
        selectedValues: [generateAllTag()],
      },
      startupItems: {
        fields: ['DEV_STATUS_ORG_IS_STARTUPS'],
        selectedValues: [],
      },
    },
  },
  {
    id: '14',
    logic: 'AND',
    type: 'combined',
    property: 'currentRdStatus',
    fields: {
      indicationItems: {
        fields: ['DISEASE_ID'],
        tags: [],
      },
      organizationItems: {
        fields: ['ORGANIZATION_ID_ONEID'],
        tags: [],
      },
      phaseItems: {
        fields: ['DEV_STATUS'],
        selectedValues: [generateAllTag()],
      },
      approvedCountryItems: { fields: ['COUNTRY_COLLAPSED'], selectedValues: [generateAllTag()] },
      milestoneItems: { fields: ['MILESTONE_ID'], selectedValues: [generateAllTag()] },
      dateItems: {
        fields: ['DEV_STATUS_TIME'],
        selectedValues: [],
      },
    },
  },
] as IFieldGroup[])

const logicOptions = [
  {
    label: 'AND',
    value: 'AND',
  },
  {
    label: 'OR',
    value: 'OR',
  },
  {
    label: 'NOT',
    value: 'NOT',
  },
]
export function useDrugFinderExpertSearch() {
  const logicConfig = ref<Record<Extract<ILogicKey, 'allFieldItems'>, E_QUERY_ITEM_CONDITION>>({
    allFieldItems: E_QUERY_ITEM_CONDITION.ANY,
  })
  const { locale, ts } = useLocale()
  const localeValue = ref<string>(locale.value)
  const renderPropertyOptions = [
    {
      display_name_cn: '药物属性',
      display_name_en: 'Drug Properties',
      value: 'drugProperties',
      options: [
        {
          display_name_cn: '药物',
          display_name_en: 'Drug',
          value: 'drug',
          disabled: false,
        },
        {
          display_name_cn: '药物类型',
          display_name_en: 'Drug Type',
          value: 'drugType',
          disabled: false,
        },
        {
          display_name_cn: '靶点',
          display_name_en: 'Target',
          value: 'target',
          disabled: false,
        },
        {
          display_name_cn: '适应症',
          display_name_en: 'Indication',
          value: 'indication',
          disabled: false,
        },
        {
          display_name_cn: '机构',
          display_name_en: 'Organization',
          value: 'organization',
          disabled: false,
        },
        {
          display_name_cn: '机构属性',
          display_name_en: 'Organization Attributes',
          value: 'organizationProperty',
          disabled: false,
        },
        {
          display_name_cn: '最高阶段',
          display_name_en: 'Highest Phase',
          value: 'highestPhase',
          disabled: false,
        },
        {
          display_name_cn: '作用机制',
          display_name_en: 'MOA',
          value: 'moa',
          disabled: false,
        },
        {
          display_name_cn: '化学修饰',
          display_name_en: 'Chemical Modification',
          value: 'chemicalModification',
          disabled: false,
        },
        {
          display_name_cn: '里程碑',
          display_name_en: 'Milestone',
          value: 'milestone',
          disabled: false,
        },
        {
          display_name_cn: '存在交易',
          display_name_en: 'Deal',
          value: 'deal',
          disabled: false,
        },
        {
          display_name_cn: 'ATC',
          display_name_en: 'ATC',
          value: 'atc',
          disabled: false,
        },
        {
          display_name_cn: '关键词',
          display_name_en: 'Keyword',
          value: 'keyword',
          disabled: false,
        },
      ],
    },
    {
      display_name_cn: '研发状态',
      display_name_en: 'R&D Status',
      value: 'rdStatus',
      options: [
        {
          display_name_cn: '当前研发状态',
          display_name_en: 'Current R&D Status',
          value: 'currentRdStatus',
          disabled: false,
        },
      ],
    },
  ]
  // 将fieldGroupsBasis转换为查询字符串的函数
  const convertToQueryString = (groups: IFieldGroup[]) => {
    if (!groups || groups.length === 0) return ''
    const queryString = groups
      .map((group) => {
        // 处理每个group内部的fields
        const fieldQueries = Object.entries(group.fields)
          .filter(([_, field]) => {
            // 过滤掉空值
            return (
              (field.tags && field.tags.length > 0) ||
              (field.selectedValues && field.selectedValues.length > 0 && (field.selectedValues[0] as IBAcTag).id !== 'All')
            )
          })
          .map(([fieldKey, field]) => {
            const fieldName = getFieldDisplayName(fieldKey)

            if (field.tags && field.tags.length > 0) {
              // 处理tags，用OR连接
              const tagValues = field.tags
                .map((tag) => {
                  if (fieldKey === 'atcItems') {
                    return `"${tag.atc_code || tag[`display_name_${localeValue.value}` as keyof IBAcTag] || tag.id}"`
                  }
                  return `"${tag[`display_name_${localeValue.value}` as keyof IBAcTag] || tag.id}"`
                })
                .join(' OR ')
              return `${fieldName}: (${tagValues})`
            } else if (field.selectedValues && field.selectedValues.length > 0 && (field.selectedValues[0] as IBAcTag).id !== 'All') {
              // 处理selectedValues，用OR连接
              const values = field.selectedValues
                .map((val) => `"${val[`display_name_${localeValue.value}` as keyof IBAcTag] || val.id}"`)
                .join(' OR ')
              return `${fieldName}: (${values})`
            }
            return ''
          })
          .filter((query) => query !== '')
          .join(' AND ')

        return { query: fieldQueries, group }
      })
      .filter(({ query }) => query !== '')
      .map(({ query, group: { type, property, logic, fields } }, index) => {
        if (index === 0 && property !== 'milestone') return query
        // type === 'combined' 时，query 需要用括号包裹
        if (type === 'combined') {
          const propertyInfo = renderPropertyOptions.flatMap((item) => item.options).find((item) => item.value === property)
          // 里程碑日期字段需要单独处理
          if (property === 'milestone') {
            if (fields.milestoneDate.selectedValues?.length) {
              const milestoneDateQuery = fields.milestoneDate.selectedValues[0][`display_name_${localeValue.value}` as keyof IBAcTag]
              const milestoneItemsQuery = fields.milestoneItems.selectedValues?.map((val) => {
                return `${val[`display_name_${localeValue.value}` as keyof IBAcTag]}: "${milestoneDateQuery}"`
              })
              return `${index === 0 ? '' : logic} ${
                propertyInfo?.[`display_name_${localeValue.value}` as keyof typeof propertyInfo]
              }: (${milestoneItemsQuery?.join(' OR ')})`
            }
            return ''
          }
          // 适应症字段需要单独处理
          if (property === 'indication' && !fields.indicationItems.tags?.length) {
            return ''
          }
          return `${logic} ${propertyInfo?.[`display_name_${localeValue.value}` as keyof typeof propertyInfo]}: (${query})`
        }
        return `${logic} ${query}`
      })
      .join(' ')

    return queryString
  }

  // 获取字段显示名称
  const getFieldDisplayName = (fieldKey: string): string => {
    const fieldNameMap: { [key: string]: string } = {
      drugItems: localeValue.value === 'cn' ? '药物' : 'Drug',
      drugTypeItems: localeValue.value === 'cn' ? '药物类型' : 'Drug Type',
      targetItems: localeValue.value === 'cn' ? '靶点' : 'Target',
      atcItems: localeValue.value === 'cn' ? 'ATC' : 'ATC',
      modificationItems: localeValue.value === 'cn' ? '化学修饰' : 'Chemical Modification',
      keyword: localeValue.value === 'cn' ? '关键词' : 'Keyword',
      dealExistItems: localeValue.value === 'cn' ? '存在交易' : 'Deal',
      indicationItems: localeValue.value === 'cn' ? '适应症' : 'Indication',
      indicationPhaseItems: localeValue.value === 'cn' ? '适应症状态' : 'Indication Status',
      actionItems: localeValue.value === 'cn' ? '作用方式' : 'Action',
      mechanismItems: localeValue.value === 'cn' ? '作用机制' : 'Mechanism',
      milestoneItems: localeValue.value === 'cn' ? '里程碑' : 'Milestone',
      milestoneDate: localeValue.value === 'cn' ? '里程碑日期' : 'Milestone Date',
      highestPhaseItems: localeValue.value === 'cn' ? '最高阶段' : 'Highest Phase',
      organizationItems: localeValue.value === 'cn' ? '机构' : 'Organization',
      organizationTypeItems: localeValue.value === 'cn' ? '机构类型' : 'Organization Type',
      orgRoleItems: localeValue.value === 'cn' ? '机构角色' : 'Org Role',
      employeeNumberItems: localeValue.value === 'cn' ? '员工数量' : 'Employee Number',
      startupItems: localeValue.value === 'cn' ? '初创公司' : 'Startup',
      approvedCountryItems: localeValue.value === 'cn' ? '国家/地区' : 'Country/Location',
      phaseItems: localeValue.value === 'cn' ? '研发状态（基于适应症）' : 'Phase (By Indication)',
      dateItems: localeValue.value === 'cn' ? '日期' : 'Date',
    }
    return fieldNameMap[fieldKey] || fieldKey
  }

  const timeRange = {
    '1-w': getLast7daysRange(),
    '1-M': getLast30daysRange(),
    '3-M': getLast3MonthsRange(),
    '1-y': getOneYearRange(),
  }
  // 将fieldGroupsBasis转换为类似exampleQueryItems的查询结构，支持层级嵌套逻辑处理
  const convertToStandardQueryItems = (groups: IFieldGroup[]) => {
    if (!groups || groups.length === 0) return null

    // 先提取所有有效的字段查询项
    const allFieldQueries: Array<{
      query: any
      logic: string
      originalIndex: number
      originalGroup: IFieldGroup
    }> = []

    groups.forEach((group, index) => {
      if (group.type === 'combined') {
        // 组合字段：所有字段用 AND 连接
        const combinedFieldQueries: any[] = []
        Object.entries(group.fields).forEach(([fieldKey, fieldData]) => {
          const queries = createFieldQueries(fieldKey, fieldData, group)
          combinedFieldQueries.push(...queries)
        })
        // 里程碑字段需要单独处理
        if (group.property === 'milestone') {
          const milestoneItemsQuery = [
            {
              type: 'field' as const,
              fields: group.fields.milestoneItems.selectedValues?.map((val) => val.id),
              condition: 'any' as const,
              value: group.fields.milestoneDate.selectedValues?.map((val) => ({
                display_name_cn: val.display_name_cn || val.id,
                display_name_en: val.display_name_en || val.id,
                type: 'range',
                value: val.id,
                id: val.id,
                from: timeRange[val.id as keyof typeof timeRange]?.from,
                to: timeRange[val.id as keyof typeof timeRange]?.to,
              })),
              _meta: {
                originalIndex: parseInt(group.id),
                originalLogic: group.logic,
                originalType: group.type,
                originalProperty: group.property,
                originalId: group.id,
                fieldKey: 'milestoneDate',
              },
            },
          ]
          combinedFieldQueries.push(...milestoneItemsQuery)
        }

        if (combinedFieldQueries.length > 0) {
          // 如果只有一个字段，直接使用该字段；如果有多个字段，包装成group
          const combinedQuery =
            combinedFieldQueries.length === 1
              ? combinedFieldQueries[0]
              : {
                  type: 'group' as const,
                  must: combinedFieldQueries,
                }

          allFieldQueries.push({
            query: combinedQuery,
            logic: index === 0 ? 'AND' : group.logic,
            originalIndex: index,
            originalGroup: group,
          })
        }
      } else {
        // 简单字段
        Object.entries(group.fields).forEach(([fieldKey, fieldData]) => {
          if (!fieldData || (!fieldData.tags?.length && !fieldData.selectedValues?.length)) {
            return
          }
          const fieldQueries = createFieldQueries(fieldKey, fieldData, group)
          fieldQueries.forEach((query) => {
            allFieldQueries.push({
              query,
              logic: index === 0 ? 'AND' : group.logic,
              originalIndex: index,
              originalGroup: group,
            })
          })
        })
      }
    })

    if (allFieldQueries.length === 0) return null

    // 根据逻辑关系构建层级结构
    return buildLogicalStructure(allFieldQueries as any)
  }
  // 创建字段查询项
  const createFieldQueries = (
    fieldKey: string,
    fieldData: { tags?: IBAcTag[]; selectedValues?: IBAcTag[]; fields?: string[] },
    group: IFieldGroup
  ) => {
    // 适应症阶段和组织角色字段不进行查询, milestoneDate字段不进行查询, milestoneItems里程碑字段不进行查询
    if (fieldKey === 'indicationPhaseItems' || fieldKey === 'orgRoleItems' || fieldKey === 'milestoneDate' || fieldKey === 'milestoneItems') {
      return []
    }

    const queries: any[] = []

    // 处理tags类型的字段
    if (fieldData.tags && fieldData.tags.length > 0) {
      let fieldQuery = {
        type: 'field' as const,
        fields: fieldData.fields || [getFieldMapping(fieldKey)],
        condition: 'any' as const,
        value: fieldData.tags.map((tag: IBAcTag) => {
          const display_name_cn = fieldKey === 'atcItems' ? tag.atc_code || tag.display_name_cn || tag.id : tag.display_name_cn || tag.id
          const display_name_en = fieldKey === 'atcItems' ? tag.atc_code || tag.display_name_en || tag.id : tag.display_name_en || tag.id
          const type = getFieldType(fieldKey)
          return {
            ...tag,
            display_name_cn,
            display_name_en,
            type,
            id: tag.id || tag.display_name_en || tag.display_name_cn,
            search_strategy: tag.search_strategy || getSearchStrategy(fieldKey),
            value: tag.id || tag.display_name_en || tag.display_name_cn,
          }
        }),
        _meta: {
          originalIndex: parseInt(group.id),
          originalLogic: group.logic,
          originalType: group.type,
          originalProperty: group.property,
          originalId: group.id,
          fieldKey,
        },
      }

      // 如果tag有自定义的searchQuery，使用它
      if (fieldData.tags[0]?.searchQuery) {
        fieldQuery = { ...fieldData.tags[0].searchQuery, _meta: fieldQuery._meta } as any
      }
      queries.push(fieldQuery)
    }

    // 处理selectedValues类型的字段
    if (fieldData.selectedValues && fieldData.selectedValues.length > 0) {
      const validValues = fieldData.selectedValues.filter((val: any) => (typeof val === 'object' ? val.id !== 'All' : val !== 'All'))

      if (validValues.length > 0) {
        const fieldQuery = {
          type: 'field' as const,
          fields: fieldData.fields || [getFieldMapping(fieldKey)],
          condition: 'any' as const,
          value: validValues.map((val: any) => {
            if (typeof val === 'object') {
              const display_name_cn = val.display_name_cn || val.id
              const display_name_en = val.display_name_en || val.id
              const type = getFieldType(fieldKey)
              if (type === 'range') {
                const from = timeRange[val.id as keyof typeof timeRange]?.from || Number(val.id.split('-')[0])
                const to = timeRange[val.id as keyof typeof timeRange]?.to || Number(val.id.split('-')[1])
                return {
                  ...val,
                  display_name_cn,
                  display_name_en,
                  type,
                  from,
                  to,
                  id: val.id || val.display_name_en || val.display_name_cn,
                  search_strategy: val.search_strategy || getSearchStrategy(fieldKey),
                  value: val.id || val.display_name_en || val.display_name_cn,
                }
              }
              return {
                ...val,
                display_name_cn: val.display_name_cn || val.id,
                display_name_en: val.display_name_en || val.id,
                type,
                value: val.id,
                id: val.id,
                search_strategy: val.search_strategy || getSearchStrategy(fieldKey),
              }
            }
            return {
              ...val,
              display_name_cn: val,
              display_name_en: val,
              type: 'text',
              value: val,
            }
          }),
          _meta: {
            originalIndex: parseInt(group.id),
            originalLogic: group.logic,
            originalType: group.type,
            originalProperty: group.property,
            originalId: group.id,
            fieldKey,
          },
        }
        queries.push(fieldQuery)
      }
    }

    return queries
  }

  // 根据逻辑关系构建层级结构
  const buildLogicalStructure = (
    fieldQueries: Array<{
      query: any
      logic: 'AND' | 'OR' | 'NOT'
      originalIndex: number
      originalGroup: IFieldGroup
    }>
  ) => {
    if (fieldQueries.length === 0) return null

    // 从左到右处理，构建层级结构
    let currentResult = null

    for (let i = 0; i < fieldQueries.length; i++) {
      const currentItem = fieldQueries[i]
      let currentQuery = currentItem.query

      // 处理NOT逻辑：设置condition为none
      if (currentItem.logic === 'NOT') {
        currentQuery = {
          ...currentQuery,
          condition: 'none' as E_QUERY_ITEM_CONDITION,
        } as any
      }

      if (currentResult === null) {
        // 第一个项目，直接设置为当前结果
        currentResult = currentQuery
      } else {
        if (currentItem.logic === 'OR') {
          // OR逻辑：将前面的结果和当前项组合成any结构
          currentResult = {
            type: 'group' as const,
            any: [currentResult, currentQuery],
          }
        } else {
          // AND逻辑：将前面的结果和当前项组合成must结构
          if (currentResult.type === 'group' && currentResult.must) {
            // 如果当前结果已经是group的must结构，直接添加
            currentResult.must.push(currentQuery)
          } else {
            // 否则创建新的group结构
            currentResult = {
              type: 'group' as const,
              must: [currentResult, currentQuery],
            }
          }
        }
      }
    }

    // 如果结果不是group结构，包装成group
    if (currentResult && currentResult.type !== 'group') {
      currentResult = {
        type: 'group' as const,
        must: [currentResult],
      }
    }
    return currentResult
  }

  // 字段映射函数
  const getFieldMapping = (fieldKey: string): string => {
    const fieldMappings: { [key: string]: string } = {
      drugItems: 'DRUG_ID',
      drugTypeItems: 'DRUG_TYPE',
      targetItems: 'TARGET_ID',
      atcItems: 'ATC_CODE',
      modificationItems: 'MODIFICATION_ID',
      keyword: 'ALL_FIELDS',
      dealExistItems: 'EXIST_DEAL',
      indicationItems: 'DISEASE_ID',
      indicationPhaseItems: 'INDICATION_PHASE',
      actionItems: 'ACTION_ID',
      mechanismItems: 'MECHANISM_ACTION_ID',
      milestoneItems: 'MILESTONE_ID',
      milestoneDate: 'MILESTONE_DATE',
      highestPhaseItems: 'GLOBAL_HIGHEST_DEV_STATUS',
      organizationItems: 'ORGANIZATION_ID_ONEID',
      organizationTypeItems: 'ORG_TYPE_ONEID',
      orgRoleItems: 'DEV_ORG_TYPE_ONEID',
      employeeNumberItems: 'ORG_EMP_NUMBER_GENERAL_ONEID',
      startupItems: 'DEV_STATUS_ORG_IS_STARTUPS',
      approvedCountryItems: 'COUNTRY_COLLAPSED',
      phaseItems: 'DEV_STATUS',
      dateItems: 'DEV_STATUS_TIME',
    }
    return fieldMappings[fieldKey] || fieldKey.toUpperCase()
  }

  // 字段类型映射
  const getFieldType = (fieldKey: string): string => {
    const typeMap: { [key: string]: string } = {
      drugItems: 'drug',
      organizationItems: 'organization',
      indicationItems: 'disease',
      atcItems: 'text',
      targetItems: 'target',
      actionItems: 'text',
      mechanismItems: 'text',
      drugTypeItems: 'text',
      modificationItems: 'text',
      keyword: 'text',
      dateItems: 'range',
    }
    return typeMap[fieldKey] || 'text'
  }

  // 搜索策略映射
  const getSearchStrategy = (fieldKey: string): string => {
    const strategyMap: { [key: string]: string } = {
      drugItems: 'ID',
      organizationItems: 'ID_ROLLUP',
      indicationItems: 'ID_ROLLUP',
      targetItems: 'ID_ROLLUP',
      actionItems: 'ID_ROLLUP',
      mechanismItems: 'ID',
      modificationItems: 'ID_ROLLUP',
      atcItems: 'ID_ROLLUP',
      drugTypeItems: 'ID_ROLLUP',
    }
    return strategyMap[fieldKey] || 'ID'
  }
  type IQueryItemValue = { id: string; display_name_cn: string; display_name_en: string; value: string }
  // 根据meta信息创建字段组
  const createGroupFromMeta = (meta: any, item: any): IFieldGroup => {
    const group: IFieldGroup = {
      id: meta.originalId,
      logic: meta.originalLogic,
      type: meta.originalType,
      property: meta.originalProperty,
      fields: {},
    }

    // 恢复字段数据
    if (item.value && item.value.length > 0) {
      // 从fieldGroupsBasis.fields中获取fieldKey对应的tag, 如果存在,则恢复为tags格式, 如果不存在,则恢复为selectedValues格式
      const isTagType = fieldGroupsBasis.find((field) => field.fields[meta.fieldKey]?.tags)

      if (isTagType) {
        // 恢复为tags格式
        group.fields[meta.fieldKey] = {
          fields: item.fields,
          tags: item.value.map((val: IQueryItemValue) => ({
            ...val,
            id: val.id || val.value,
            display_name_cn: val.display_name_cn,
            display_name_en: val.display_name_en,
          })),
        }
      } else {
        // 里程碑字段需要单独处理
        if (meta.fieldKey === 'milestoneDate') {
          group.fields[meta.fieldKey] = {
            fields: ['MILESTONE_DATE'],
            selectedValues: item.value.map((val: IQueryItemValue) => ({
              ...val,
              id: val.id || val.value,
              display_name_cn: val.display_name_cn,
              display_name_en: val.display_name_en,
            })),
          }
          group.fields.milestoneItems = {
            fields: ['MILESTONE_ID'],
            selectedValues: item.fields.map((val: string) => generateHardData('milestoneItems').find((o) => o.id === val)),
          }
        } else {
          // 恢复为selectedValues格式
          group.fields[meta.fieldKey] = {
            fields: item.fields,
            selectedValues: item.value.map((val: IQueryItemValue) => ({
              ...val,
              id: val.id || val.value,
              display_name_cn: val.display_name_cn,
              display_name_en: val.display_name_en,
            })),
          }
        }
      }
    }

    return group
  }

  // 将标准查询项恢复为fieldGroupsBasis格式
  const convertFromStandardQueryItems = (queryItems: any): IFieldGroup[] => {
    if (!queryItems) return []

    const result: IFieldGroup[] = []

    // 递归处理查询项
    const processQueryItem = (item: any, defaultLogic: 'AND' | 'OR' | 'NOT' = 'AND'): void => {
      if (item.type === 'field' && item._meta) {
        // 处理单个字段项
        const group = transformFieldFromQueryItems(createGroupFromMeta(item._meta, item), item, item._meta)

        // 根据condition判断逻辑
        if (item.condition === 'none') {
          group.logic = 'NOT'
        } else {
          group.logic = defaultLogic
        }
        group._meta = item._meta
        result.push(group)
      } else if (item.type === 'group') {
        if (item.must) {
          // 处理must数组：AND逻辑
          item.must.forEach((subItem: any, index: number) => {
            processQueryItem(subItem, index === 0 ? 'AND' : 'AND')
          })
        } else if (item.any) {
          // 处理any数组：OR逻辑
          item.any.forEach((subItem: any, index: number) => {
            processQueryItem(subItem, index === 0 ? 'AND' : 'OR')
          })
        }
      }
    }

    // 处理根级别的查询项
    if (queryItems.type === 'group' && queryItems.must) {
      queryItems.must.forEach((item: any, index: number) => {
        processQueryItem(item, index === 0 ? 'AND' : 'AND')
      })
    } else if (queryItems.type === 'group' && queryItems.any) {
      queryItems.any.forEach((item: any, index: number) => {
        processQueryItem(item, index === 0 ? 'AND' : 'OR')
      })
    } else {
      processQueryItem(queryItems, 'AND')
    }
    // result 按字段id相等,type=combined的从新组合group
    const newResult: IFieldGroup[] = []
    result.forEach((item) => {
      const fieldGroup = fieldGroupsBasis.find((g) => g.property === item.property && g.type === 'combined')
      const index = newResult.findIndex((i) => i.id === item.id)
      if (index === -1) {
        if (item.type === 'combined') {
          item.fields = {
            ...cloneDeep(fieldGroup?.fields),
            ...item.fields,
          }
        }
        newResult.push(item)
      } else {
        // 已存在则合并fields
        newResult[index].fields = {
          ...newResult[index].fields,
          ...item.fields,
        }
      }
    })
    console.log('result-convertFromStandardQueryItems', result)
    // 处理combined类型的数据, 恢复为fieldGroupsBasis格式，
    // 并恢复logic，selectedValues
    // 针对indication和organization的selectedValues进行处理
    const translateNewResult = newResult.map((item) => {
      const fieldGroup = fieldGroupsBasis.find((g) => g.property === item.property && g.type === 'combined')
      if (fieldGroup) {
        // 新增分组
        if (fieldGroup) {
          item.logic = item._meta.originalLogic
          // 合并fields，优先保留item中的数据
          // indication 相关逻辑
          if (item.property === 'indication') {
            const indicationField = item.fields.indicationItems?.fields?.[0]
            let phaseId = 'All'
            if (item.fields.indicationItems?.fields?.length === 2) {
              phaseId = 'All'
            } else if (indicationField === 'RESEARCH_DISEASE') {
              phaseId = 'Active'
            } else if (indicationField === 'NON_RESEARCH_DISEASE') {
              phaseId = 'Inactive'
            }
            item.fields.indicationPhaseItems.selectedValues = generateHardData('indicationPhaseItems').filter((o) => o.id === phaseId)
          }

          // organization 相关逻辑
          if (item.property === 'organization') {
            // 根据_meta.fieldKey判断是哪个字段
            const orgField = item.fields.organizationItems?.fields
            const orgRoleIds: string[] = []

            orgField?.forEach((o) => {
              if (o === 'LICENSE_ORG_ID_ONEID') {
                orgRoleIds.push('EquityInstitutions')
              } else if (o === 'ORIGINAL_DEV_ORG_ID_ONEID') {
                orgRoleIds.push('Originator')
              } else if (o === 'DEV_ORG_ID_ONEID') {
                orgRoleIds.push('Active')
              } else if (o === 'NON_DEV_ORG_ID_ONEID') {
                orgRoleIds.push('Inactive')
              }
            })
            item.fields.orgRoleItems.selectedValues = generateHardData('orgRoleItems').filter((o) => {
              if (orgRoleIds.length === generateHardData('orgRoleItems').length - 1) {
                return o.id === 'All'
              }
              return orgRoleIds.includes(o.id)
            })
          }
        }
      }
      return item
    })
    console.log('newResult-convertFromStandardQueryItems-newResult', translateNewResult, newResult)

    // 按原始ID排序
    return translateNewResult.sort((a, b) => parseInt(a.id) - parseInt(b.id))
  }
  const transformFieldFromQueryItems = (group: IFieldGroup, item: IQueryItemField, meta: any) => {
    const data = group.fields
    const fieldKey = meta?.fieldKey as DrugFinderInputKey
    if (!fieldKey) return group

    // 恢复高级检索Tab的数据
    if (item.type === 'field') {
      // 非hot的数据走这边构造
      const fields = item.fields

      const config = drugFinderExpertReverseMap[fields[0] as keyof typeof drugFinderExpertReverseMap]
      if (config) {
        const items = item.value
        if (config.key === 'drugItems' && items.length > maxDrugCount) {
          showSingleToast({
            message: ts('common.drugLimitedMessage', { count: maxDrugCount }) as string,
            type: 'info',
          })
        }
        if (config.dataType === 'input') {
          const newItems = fieldKey === 'atcItems' ? generateATCInputItem(item, config.icon) : generateInputItem(item, config.icon)
          data[fieldKey] = {
            fields: item.fields,
            tags: newItems,
          }
        }
        if (config.dataType === 'tag') {
          const newItems = generateTagItem(item)
          if (config.key === 'regulationItems') {
            data[fieldKey] = {
              fields: item.fields,
              selectedValues: newItems,
            }
          } else {
            data[fieldKey] = {
              fields: item.fields,
              selectedValues: newItems,
            }
          }
        }
        if (config.dataType === 'orgType') {
          const newItems = generateOrgTypeItem(item)
          data[fieldKey] = {
            fields: item.fields,
            selectedValues: newItems,
          }
        }
      }
    }
    return group
  }
  function normalTags2QueryString(data: any, key: 'allFieldItems') {
    const logicVal = logicConfig.value[key]
    let logic = 'or'
    if (logicVal === E_QUERY_ITEM_CONDITION.ALL) {
      logic = 'and'
    }
    return ((data[key] as IBAcTag[]) || [])
      .map((o) => {
        const val = o[`display_name_${locale.value}`]
        const reg = /\s/
        return reg.test(val) ? `"${val}"` : val
      })
      .join(` ${logic} `)
  }
  const handleChangeLogicVal = (fieldKey: Extract<ILogicKey, 'allFieldItems'>, val: E_QUERY_ITEM_CONDITION) => {
    logicConfig.value[fieldKey] = val
  }

  const convertFromStandardQueryItemsToQueryString = (queryItems: any, locale?: string): string => {
    localeValue.value = locale || localeValue.value
    const result = convertFromStandardQueryItems(queryItems)
    return convertToQueryString(result)
  }
  /**
   * query input数据转成tag
   * @param oldItem
   * @param icon
   * @returns
   */
  function generateATCInputItem(oldItem: IQueryItemField, iconName = '') {
    const items = oldItem.value
    const res: IListItem[] = []
    let icon = iconName
    for (const item of items as any) {
      let id = ''
      if ('value' in item) {
        id = item.value as string
      }
      if ('id' in item) {
        id = item.id
      }
      if (id) {
        if ('icon' in item) {
          icon = item.icon
        }
        const mockItem: IListItem = {
          ...item,
          display_name_cn: item.display_name_cn,
          display_name_en: item.display_name_en,
          id,
          icon,
        }
        if ('search_strategy' in item) {
          mockItem.icon =
            item.search_strategy?.toUpperCase() === 'ID_ROLLUP' || item.search_strategy?.toUpperCase() === 'ANS_ID_ROLLUP' ? 'Tree' : icon
          if (item.search_strategy === 'KEYWORD' || item.search_strategy === 'ANS_KEYWORD') {
            mockItem.icon = ''
          }
        }
        res.push(mockItem)
      }
    }
    console.log('res-generateATCInputItem', res)
    return res
  }

  return {
    logicConfig,
    convertToQueryString,
    convertToStandardQueryItems,
    convertFromStandardQueryItems,
    convertFromStandardQueryItemsToQueryString,
    normalTags2QueryString,
    handleChangeLogicVal,
    logicOptions,
    renderPropertyOptions,
    generateATCInputItem,
  }
}
